import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr, fdate, money } from "../api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import InfoTip from "../components/InfoTip";
import { StatusPill } from "../components/bits";
import { COUNTRIES, CURRENCIES } from "../constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const inputCls = "cr-input";

function ProfileTab({ user, reload }) {
  const [form, setForm] = useState({ name: user.name || "", phone: user.phone || "", country: user.country || "", company_name: user.company_name || "", vat_number: user.vat_number || "", timezone: user.timezone || "Europe/London", language: user.language || "en", billing_currency: user.billing_currency || "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    try { await api.put("/account/profile", form); toast.success("Profile saved"); reload(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <section className="cr-card !p-5 space-y-3" data-testid="profile-tab">
      <h2 className="text-lg font-bold">Profile</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="profile-name" value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} />
        <input data-testid="profile-phone" value={form.phone} onChange={set("phone")} placeholder="Phone (+44…)" className={inputCls} />
        <select data-testid="profile-country" value={form.country} onChange={set("country")} className={inputCls}>
          <option value="">Country</option>
          {COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
        </select>
        <input data-testid="profile-timezone" value={form.timezone} onChange={set("timezone")} placeholder="Timezone (e.g. Europe/Berlin)" className={inputCls} />
        {user.role === "company" && <>
          <input data-testid="profile-company" value={form.company_name} onChange={set("company_name")} placeholder="Company name" className={inputCls} />
          <input data-testid="profile-vat" value={form.vat_number} onChange={set("vat_number")} placeholder="VAT number" className={inputCls} />
          <select data-testid="profile-currency" value={form.billing_currency} onChange={set("billing_currency")} className={inputCls}>
            <option value="">Invoice currency (auto)</option>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </>}
      </div>
      <button data-testid="profile-save-btn" onClick={save} className="bg-black text-white hover:bg-zinc-800 font-medium transition-all px-5 py-2.5 rounded-lg text-sm">Save</button>
    </section>
  );
}

function DocumentsTab() {
  const [docs, setDocs] = useState([]);
  const [required, setRequired] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", doc_type: "driving_licence", expiry_date: "", file_data: null });
  const load = () => {
    api.get("/documents").then((r) => setDocs(r.data.documents)).catch(() => {});
    api.get("/documents/required").then((r) => setRequired(r.data.required)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const upload = async () => {
    try { await api.post("/documents", form); toast.success("Document uploaded — pending verification"); setOpen(false); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const toB64 = (f) => { const r = new FileReader(); r.onload = () => setForm((p) => ({ ...p, file_data: r.result })); r.readAsDataURL(f); };
  return (
    <section className="cr-card !p-5" data-testid="documents-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold flex items-center">Documents<InfoTip text="Licence, insurance and compliance documents. Verified by our team; we remind you before expiry." /></h2>
        <button data-testid="doc-upload-open" onClick={() => setOpen(true)} className="bg-[#1C1B1A] text-white text-xs font-medium px-4 py-2 rounded-full hover:bg-[#2C2B2A] transition-all">Upload</button>
      </div>
      {required.length > 0 && (
        <div className="mb-4 space-y-1.5" data-testid="required-docs-checklist">
          {required.map((r) => (
            <div key={r.doc_type} className="flex items-center justify-between border border-zinc-100 rounded-lg px-3 py-2">
              <span className="text-xs font-medium capitalize">{r.doc_type.replace(/_/g, " ")}</span>
              {r.verified ? <StatusPill status="approved" /> : r.uploaded ? <StatusPill status="pending" /> : (
                <button data-testid={`required-upload-${r.doc_type}`} onClick={() => { setForm({ ...form, doc_type: r.doc_type, name: r.doc_type.replace(/_/g, " ") }); setOpen(true); }}
                  className="text-xs font-semibold text-black underline underline-offset-2">Upload</button>
              )}
            </div>
          ))}
        </div>
      )}
      {docs.length === 0 && required.length === 0 && <p className="text-xs text-zinc-400">No documents yet.</p>}
      <div className="divide-y divide-zinc-100">
        {docs.map((d) => (
          <div key={d.id} data-testid={`doc-${d.id}`} className="py-3 flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold">{d.name}</p>
              <p className="text-[11px] text-zinc-500 capitalize">{d.doc_type.replace(/_/g, " ")}{d.expiry_date ? ` · expires ${fdate(d.expiry_date)}` : ""}</p>
            </div>
            <div className="flex items-center gap-2">
              {d.file_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${d.file_url}`} target="_blank" rel="noreferrer" className="text-xs text-[#0A0A0A] font-medium">View</a>}
              <StatusPill status={d.verified ? "approved" : "pending"} />
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="doc-dialog">
          <DialogHeader><DialogTitle>Upload document</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <input data-testid="doc-name" placeholder="Document name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            <select data-testid="doc-type" value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} className={inputCls}>
              <option value="driving_licence">Driving licence</option><option value="insurance">Insurance</option>
              <option value="transport_licence">EU transport licence</option><option value="cmr_insurance">CMR insurance</option>
              <option value="company_registration">Company registration</option><option value="vat_certificate">VAT certificate</option>
              <option value="right_to_work">Right to work</option>
              <option value="other">Other</option>
            </select>
            <div><label className="text-xs font-semibold text-[#78756E] uppercase tracking-wide">Expiry date</label>
              <input data-testid="doc-expiry" type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className={inputCls} /></div>
            <input data-testid="doc-file" type="file" accept="image/*,application/pdf" onChange={(e) => e.target.files[0] && toB64(e.target.files[0])} className="text-xs" />
            <button data-testid="doc-submit-btn" onClick={upload} disabled={!form.name} className="w-full bg-[#0A0A0A] text-white hover:bg-[#26272A] font-semibold transition-all px-5 py-2.5 rounded-lg text-sm disabled:opacity-40">Upload</button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function AutoInvoicePref() {
  const [on, setOn] = useState(null);
  useEffect(() => { api.get("/account/prefs").then((r) => setOn(!!r.data.prefs?.invoice_pdf_auto)).catch(() => setOn(false)); }, []);
  const toggle = async () => {
    try {
      const { data } = await api.put("/account/prefs", { invoice_pdf_auto: !on });
      setOn(!!data.prefs.invoice_pdf_auto);
      toast.success(data.prefs.invoice_pdf_auto ? "Auto PDF invoices on — every completed job emails you the invoice" : "Auto PDF invoices off");
    } catch (e) { toast.error(fmtErr(e)); }
  };
  if (on === null) return null;
  return (
    <section className="cr-card !p-5 mb-4" data-testid="auto-invoice-pref">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold mb-1 flex items-center">Automatic PDF invoices<InfoTip text="When a job is delivered, the invoice is generated as a PDF and emailed to you automatically. Requires a plan that includes it (or a perk granted by the CourierRadar team)." /></h2>
          <p className="text-xs text-zinc-400">Sent to your email the moment a job completes — no manual downloading.</p>
        </div>
        <button data-testid="auto-invoice-toggle" onClick={toggle}
          className={`shrink-0 px-4 py-2 rounded-full text-xs font-semibold transition-colors ${on ? "bg-[#1C1B1A] text-white" : "bg-[#F6F3EA] text-[#78756E] border border-[#E8E4D9]"}`}>
          {on ? "On" : "Off"}
        </button>
      </div>
    </section>
  );
}

function NotificationsTab() {
  const [data, setData] = useState({ events: [], prefs: [] });
  useEffect(() => { api.get("/notifications/prefs").then((r) => setData(r.data)).catch(() => {}); }, []);
  const prefFor = (ev) => data.prefs.find((p) => p.event === ev) || { inapp: true, email: true };
  const toggle = async (ev, channel) => {
    const cur = prefFor(ev);
    const next = { event: ev, inapp: channel === "inapp" ? !cur.inapp : cur.inapp, email: channel === "email" ? !cur.email : cur.email };
    await api.put("/notifications/prefs", next);
    setData({ ...data, prefs: [...data.prefs.filter((p) => p.event !== ev), next] });
  };
  const groups = {};
  data.events.forEach((ev) => {
    const cat = ev.split(".")[0];
    (groups[cat] = groups[cat] || []).push(ev);
  });
  const toggleAll = async (evs, channel, value) => {
    const next = evs.map((ev) => {
      const cur = prefFor(ev);
      return { event: ev, inapp: channel === "inapp" ? value : cur.inapp, email: channel === "email" ? value : cur.email };
    });
    await Promise.all(next.map((p) => api.put("/notifications/prefs", p)));
    setData({ ...data, prefs: [...data.prefs.filter((p) => !evs.includes(p.event)), ...next] });
  };
  return (
    <>
      <AutoInvoicePref />
      <section className="cr-card !p-5" data-testid="notif-prefs-tab">
      <h2 className="text-lg font-bold mb-1 flex items-center">Notification preferences<InfoTip text="Choose which events reach you by email or in-app. Essential security emails always send." /></h2>
      <p className="text-xs text-zinc-400 mb-4">Turning off email unsubscribes you from that event type.</p>
      <div className="grid sm:grid-cols-2 gap-3 max-h-[65vh] cr-scroll pr-1">
        {Object.entries(groups).map(([cat, evs]) => (
          <div key={cat} className="border border-[#E8E4D9] rounded-[20px] p-3.5 h-fit" data-testid={`pref-group-${cat}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold capitalize">{cat}</p>
              <div className="flex gap-2 text-[10px]">
                <button data-testid={`pref-${cat}-all-inapp`} onClick={() => toggleAll(evs, "inapp", true)} className="text-[#78756E] hover:text-[#1C1B1A] underline underline-offset-2">In-app on</button>
                <button data-testid={`pref-${cat}-all-email`} onClick={() => toggleAll(evs, "email", true)} className="text-[#78756E] hover:text-[#1C1B1A] underline underline-offset-2">Email on</button>
                <button data-testid={`pref-${cat}-all-off`} onClick={() => toggleAll(evs, "email", false)} className="text-[#A09D95] hover:text-red-600 underline underline-offset-2">Email off</button>
              </div>
            </div>
            <div className="divide-y divide-[#F6F3EA]">
              {evs.map((ev) => {
                const p = prefFor(ev);
                return (
                  <div key={ev} className="py-2 flex items-center justify-between gap-3">
                    <span className="text-xs font-medium text-zinc-700 capitalize">{ev.split(".").slice(1).join(" ").replace(/_/g, " ")}</span>
                    <div className="flex gap-3 text-[11px]">
                      <label className="flex items-center gap-1.5"><input type="checkbox" data-testid={`pref-inapp-${ev}`} checked={p.inapp} onChange={() => toggle(ev, "inapp")} className="accent-[#1C1B1A]" />In-app</label>
                      <label className="flex items-center gap-1.5"><input type="checkbox" data-testid={`pref-email-${ev}`} checked={p.email} onChange={() => toggle(ev, "email")} className="accent-[#1C1B1A]" />Email</label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      </section>
    </>
  );
}

function SecuritySection({ user }) {
  const [sessions, setSessions] = useState([]);
  const [mfaSecret, setMfaSecret] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const loadSessions = () => api.get("/auth/sessions").then((r) => setSessions(r.data.sessions)).catch(() => {});
  useEffect(() => { loadSessions(); }, []);
  return (
    <section className="cr-card !p-5 space-y-5" data-testid="security-section">
      <h2 className="text-lg font-bold">Security</h2>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-100 pb-4">
        <div>
          <p className="text-sm font-semibold">Email verification</p>
          <p className="text-xs text-zinc-500">{user.email_verified ? "Your email is verified." : "Verify your email to secure your account."}</p>
        </div>
        {user.email_verified ? <StatusPill status="approved" /> : (
          <button data-testid="resend-verification-btn" onClick={async () => { await api.post("/auth/resend-verification"); toast.success("Verification email sent"); }}
            className="bg-[#1C1B1A] text-white text-xs font-medium px-4 py-2 rounded-full hover:bg-[#2C2B2A] transition-all">Resend link</button>
        )}
      </div>
      <div className="border-b border-zinc-100 pb-4">
        <p className="text-sm font-semibold">Two-factor authentication</p>
        <p className="text-xs text-zinc-500 mb-2">{user.totp_enabled ? "Enabled — a code is required at sign in." : "Add an authenticator-app code at sign in."}</p>
        {!user.totp_enabled && !mfaSecret && (
          <button data-testid="mfa-setup-btn" onClick={async () => { const { data } = await api.post("/auth/mfa/setup"); setMfaSecret(data); }}
            className="border border-zinc-300 text-xs font-medium px-4 py-2 rounded-lg hover:bg-zinc-50">Enable MFA</button>
        )}
        {mfaSecret && !user.totp_enabled && (
          <div className="space-y-2" data-testid="mfa-setup-box">
            <p className="text-xs text-zinc-500">Add this secret to your authenticator app:</p>
            <p className="font-mono text-sm bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 select-all" data-testid="mfa-secret">{mfaSecret.secret}</p>
            <div className="flex gap-2">
              <input data-testid="mfa-enable-code" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)}
                className="border border-zinc-200 rounded-lg text-sm px-3 py-2 outline-none focus:border-black w-36 font-mono" />
              <button data-testid="mfa-enable-btn" onClick={async () => {
                try { await api.post("/auth/mfa/enable", { code: mfaCode }); toast.success("MFA enabled"); setMfaSecret(null); window.location.reload(); }
                catch (e) { toast.error(fmtErr(e)); }
              }} className="bg-[#1C1B1A] text-white text-xs font-medium px-4 py-2 rounded-full hover:bg-[#2C2B2A] transition-all">Confirm</button>
            </div>
          </div>
        )}
        {user.totp_enabled && (
          <div className="flex gap-2">
            <input data-testid="mfa-disable-code" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)}
              className="border border-zinc-200 rounded-lg text-sm px-3 py-2 outline-none focus:border-black w-36 font-mono" />
            <button data-testid="mfa-disable-btn" onClick={async () => {
              try { await api.post("/auth/mfa/disable", { code: mfaCode }); toast.success("MFA disabled"); window.location.reload(); }
              catch (e) { toast.error(fmtErr(e)); }
            }} className="border border-zinc-300 text-xs font-medium px-4 py-2 rounded-lg hover:bg-zinc-50">Disable MFA</button>
          </div>
        )}
      </div>
      <div className="border-b border-zinc-100 pb-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold">Active sessions</p>
          <button data-testid="revoke-others-btn" onClick={async () => {
            await Promise.all(sessions.filter((s) => !s.current).map((s) => api.delete(`/auth/sessions/${s.jti}`)));
            toast.success("Other sessions revoked"); loadSessions();
          }} className="text-xs text-zinc-500 hover:text-black">Revoke all others</button>
        </div>
        {sessions.map((s) => (
          <div key={s.jti} data-testid={`session-${s.jti}`} className="flex items-center justify-between text-xs py-1.5">
            <span className="text-zinc-600 truncate max-w-[240px]">{s.ua || "Unknown device"}</span>
            <span className="text-zinc-400">{fdate(s.created_at)}{s.current && " · this device"}</span>
            {!s.current && <button data-testid={`revoke-${s.jti}`} onClick={async () => { await api.delete(`/auth/sessions/${s.jti}`); loadSessions(); }} className="text-zinc-500 hover:text-black">Revoke</button>}
          </div>
        ))}
      </div>
      <div>
        <p className="text-sm font-semibold">Password</p>
        <p className="text-xs text-zinc-500 mb-2">We email you a secure reset link. All sessions are revoked after a password change.</p>
        <button data-testid="password-reset-btn" onClick={async () => { await api.post("/auth/forgot-password", { email: user.email }); toast.success(`Reset link sent to ${user.email}`); }}
          className="border border-zinc-300 text-xs font-medium px-4 py-2 rounded-lg hover:bg-zinc-50">Email me a reset link</button>
      </div>
    </section>
  );
}

function PrivacyTab({ user, logout }) {
  const [confirm, setConfirm] = useState(false);
  const doExport = async () => {
    const { data } = await api.get("/account/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "courierradar-data-export.json";
    a.click();
  };
  const doDelete = async () => {
    try { await api.delete("/account"); await logout(); window.location.href = "/"; }
    catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <section className="cr-card !p-5" data-testid="privacy-tab">
      <h2 className="text-lg font-bold mb-2">Privacy & data (GDPR)</h2>
      <p className="text-xs text-zinc-500 mb-4">Your data is processed to operate the marketplace. Location is only stored while you run an active delivery.</p>
      <div className="flex flex-wrap gap-2">
        <button data-testid="export-data-btn" onClick={doExport} className="border border-zinc-300 hover:bg-zinc-50 text-zinc-800 font-medium transition-all px-4 py-2 rounded-lg text-sm">Export my data</button>
        {!confirm ? (
          <button data-testid="delete-account-btn" onClick={() => setConfirm(true)} className="border border-red-200 text-red-600 hover:bg-red-50 font-medium transition-all px-4 py-2 rounded-lg text-sm">Delete account</button>
        ) : (
          <button data-testid="delete-account-confirm" onClick={doDelete} className="bg-red-600 text-white hover:bg-red-700 font-medium transition-all px-4 py-2 rounded-lg text-sm">Confirm permanent deletion</button>
        )}
      </div>
    </section>
  );
}

export default function AccountPage() {
  const { user, reload, logout } = useAuth();
  const [billing, setBilling] = useState(null);
  const [plans, setPlans] = useState([]);
  const [showPlans, setShowPlans] = useState(false);
  useEffect(() => {
    if (!user) return;
    api.get("/billing/my").then((r) => setBilling(r.data)).catch(() => {});
    api.get("/billing/plans").then((r) => setPlans((r.data.plans || []).filter((p) => p.role === user.role))).catch(() => {});
  }, [user]);
  if (!user) return null;
  const subscribe = async (planId) => {
    try {
      const { data } = await api.post("/billing/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-6" data-testid="account-page">
      <Seo title="Account | CourierRadar" noindex />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">{user.name}</h1>
          <p className="text-sm text-zinc-500">{user.email} · <span className="capitalize">{user.role}</span> · <StatusPill status={user.status} /></p>
        </div>
      </div>
      <section className="cr-card !p-5" data-testid="subscription-section">
        <h2 className="text-lg font-bold mb-2">Subscription</h2>
        <p className="text-sm text-zinc-600">Plan: <strong>{user.subscription_plan || "free"}</strong>
          {billing?.subscription?.status && <> · <StatusPill status={billing.subscription.status} /></>}</p>
        <div className="flex gap-2 mt-3">
          <button data-testid="manage-plan-btn" onClick={() => setShowPlans(!showPlans)}
            className="border border-zinc-300 hover:bg-zinc-50 text-zinc-800 font-medium transition-all px-4 py-2 rounded-lg text-sm">
            {showPlans ? "Hide plans" : "Change plan"}
          </button>
          {billing?.subscription?.status === "active" && (
            <button data-testid="cancel-sub-btn" onClick={async () => { await api.post("/billing/cancel"); toast.success("Subscription will end at period close"); }}
              className="border border-red-200 text-red-600 hover:bg-red-50 font-medium transition-all px-4 py-2 rounded-lg text-sm">Cancel renewal</button>
          )}
        </div>
        {showPlans && plans.length > 0 && (
          <div className="grid sm:grid-cols-3 gap-3 mt-4" data-testid="account-plan-picker">
            {plans.map((p) => (
              <div key={p.id} className="border border-zinc-200 rounded-2xl p-4 flex flex-col" data-testid={`account-plan-${p.id}`}>
                <h3 className="text-sm font-bold">{p.name}</h3>
                <p className="mt-1"><span className="text-xl font-extrabold">{money(p.price, p.currency.toUpperCase())}</span><span className="text-[11px] text-zinc-400"> /{p.interval} ex VAT</span></p>
                <ul className="mt-2 space-y-1 flex-1">{(p.features || []).slice(0, 4).map((f) => <li key={f} className="text-[11px] text-zinc-600">· {f}</li>)}</ul>
                <button data-testid={`account-subscribe-${p.id}`} onClick={() => subscribe(p.id)}
                  disabled={user.subscription_plan === p.name}
                  className="mt-3 w-full bg-black text-white hover:bg-zinc-800 disabled:opacity-40 font-medium transition-colors px-4 py-2 rounded-lg text-xs">
                  {user.subscription_plan === p.name ? "Current plan" : "Switch to this plan"}
                </button>
              </div>
            ))}
          </div>
        )}
        {billing?.payments?.length > 0 && (
          <div className="mt-4 border-t border-zinc-100 pt-3">
            <p className="text-xs font-semibold text-[#78756E] uppercase tracking-wide mb-2">Payment history</p>
            {billing.payments.slice(0, 8).map((p) => (
              <div key={p.id} className="flex justify-between text-xs py-1.5">
                <span>{p.plan_name}</span>
                <span className="text-zinc-500">{p.amount} {p.currency?.toUpperCase()} · <StatusPill status={p.payment_status} /></span>
              </div>
            ))}
          </div>
        )}
      </section>
      <ProfileTab user={user} reload={reload} />
      <DocumentsTab />
      <NotificationsTab />
      <SecuritySection user={user} />
      <PrivacyTab user={user} logout={logout} />
    </div>
  );
}
